import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ArtReproduction, Tag, Size, SizePrice, ArtistOption } from '../types';
import { trackOptionEvent } from '../services/metrics';
import ArtworkCard from '../components/gallery/ArtworkCard';
import ArtworkModal from '../components/gallery/ArtworkModal';
import GalleryFilters from '../components/gallery/GalleryFilters';
import { useToast } from '../contexts/ToastContext';
import './SearchResults.css';

interface ExtendedArtReproduction extends ArtReproduction {
  sizePrices?: SizePrice[];
  tags?: Tag[];
}

interface SelectedSize {
  size: string;
  price: number;
  isOriginal?: boolean;
}

const SearchResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const [artworks, setArtworks] = useState<ExtendedArtReproduction[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [availableSizes, setAvailableSizes] = useState<Size[]>([]);
  const [artists, setArtists] = useState<ArtistOption[]>([]);
  const [selectedArtist, setSelectedArtist] = useState<string>('');
  const [selectedSizes, setSelectedSizes] = useState<Record<number, SelectedSize>>({});
  const [selectedArtwork, setSelectedArtwork] = useState<ExtendedArtReproduction | null>(null);
  const [showArtworkModal, setShowArtworkModal] = useState<boolean>(false);
  const [addingToCart, setAddingToCart] = useState<boolean>(false);
  const { success, error: showError, warning } = useToast();

  useEffect(() => {
    fetchTags();
    fetchSizes();
    fetchArtists();
  }, []);

  const fetchSizes = async (): Promise<void> => {
    try {
      const response = await api.get<Size[]>('/sizes');
      setAvailableSizes(response.data);
    } catch (error) {
      console.error('Failed to fetch sizes:', error);
    }
  };

  const fetchTags = async (): Promise<void> => {
    try {
      const response = await api.get<Tag[]>('/tags');
      setTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const fetchArtists = async (): Promise<void> => {
    try {
      const response = await api.get<ArtistOption[]>('/art/artists');
      setArtists(response.data);
    } catch (error) {
      console.error('Failed to fetch artists:', error);
    }
  };

  const fetchArtworks = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (query) params.search = query;
      if (selectedTags.length > 0) params.tagIds = selectedTags.join(',');
      if (selectedSize) params.size = selectedSize;
      if (selectedArtist) {
        if (selectedArtist.startsWith('id:')) {
          params.artistId = selectedArtist.replace('id:', '');
        } else if (selectedArtist.startsWith('name:')) {
          params.artistName = selectedArtist.replace('name:', '');
        }
      }

      const response = await api.get<ExtendedArtReproduction[]>('/art', { params });
      setArtworks(response.data);
    } catch (error) {
      console.error('Failed to fetch artworks:', error);
    } finally {
      setLoading(false);
    }
  }, [query, selectedTags, selectedSize, selectedArtist]);

  useEffect(() => {
    fetchArtworks();
  }, [fetchArtworks]);

  const handleTagToggle = (tagId: number): void => {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleSizeSelect = (artworkId: number, sizePrice: SelectedSize): void => {
    setSelectedSizes(prev => ({
      ...prev,
      [artworkId]: sizePrice
    }));
  };

  const handleAddToCart = async (artwork: ExtendedArtReproduction): Promise<void> => {
    try {
      if (addingToCart) return;
      const selectedSize = selectedSizes[artwork.id];
      if (!selectedSize && artwork.sizePrices && artwork.sizePrices.length > 0) {
        warning('Please select a size.');
        return;
      }

      const price = selectedSize?.price || artwork.price;
      const size = selectedSize?.size || 'Standard';
      const isOriginal = selectedSize?.isOriginal || false;
      
      if (isOriginal && !artwork.isOriginalAvailable) {
        showError('This original artwork is no longer available.');
        return;
      }
      
      setAddingToCart(true);
      const productData = {
        name: `${artwork.title}${size !== 'Standard' ? ` - ${size}` : ''}`,
        description: artwork.description || `Art by ${artwork.artistName || 'Unknown Artist'}`,
        price: price,
        imageUrl: artwork.imageUrl,
        size: size,
        isCustomPrint: false,
        artReproductionId: artwork.id,
        isOriginal: isOriginal,
      };

      const productResponse = await api.post<{ id: number }>('/products', productData);
      await api.post('/cart', { productId: productResponse.data.id, quantity: 1 });
      await trackOptionEvent({
        itemType: 'Art',
        itemId: artwork.id,
        size: size,
        eventType: 'Carted'
      });
      success('Added to cart!');
    } catch (error) {
      console.error('Failed to add to cart:', error);
      showError('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleOpenArtwork = async (artwork: ExtendedArtReproduction): Promise<void> => {
    setSelectedArtwork(artwork);
    setShowArtworkModal(true);
    try {
      await trackOptionEvent({
        itemType: 'Art',
        itemId: artwork.id,
        eventType: 'Viewed'
      });
    } catch {
      // ignore
    }
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const searchTerm = (formData.get('search') as string)?.trim();
    if (searchTerm) {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
    }
  };

  return (
    <div className="search-results-page">
      <div className="container">
        <div className="search-results-header">
          <h1 className="page-title">Search Results</h1>
          <form onSubmit={handleSearchSubmit} className="search-form">
            <input
              type="text"
              name="search"
              placeholder="Search artworks, artists..."
              defaultValue={query}
              className="search-input"
            />
            <button type="submit" className="btn btn-primary">
              <i className="fas fa-search"></i> Search
            </button>
          </form>
        </div>

        {query && (
          <div className="search-query-info">
            <p>Results for: <strong>"{query}"</strong></p>
            {artworks.length > 0 && (
              <p className="result-count">{artworks.length} {artworks.length === 1 ? 'result' : 'results'} found</p>
            )}
          </div>
        )}

        <div className="gallery-layout">
          <aside className="gallery-sidebar">
            <GalleryFilters
              tags={tags}
              availableSizes={availableSizes}
              artists={artists}
              selectedTags={selectedTags}
              selectedSize={selectedSize}
              selectedArtist={selectedArtist}
              onTagToggle={handleTagToggle}
              onSizeChange={setSelectedSize}
              onArtistChange={setSelectedArtist}
            />
          </aside>
          <section className="gallery-results">
            {loading ? (
              <div className="loading">Loading artworks...</div>
            ) : artworks.length === 0 ? (
              <div className="no-results">
                {query ? (
                  <>
                    <p>No artworks found for "{query}".</p>
                    <p>Try adjusting your search or filters.</p>
                  </>
                ) : (
                  <p>Enter a search term to find artworks.</p>
                )}
              </div>
            ) : (
              <div className="artworks-grid">
                {artworks.map((artwork) => (
                  <ArtworkCard 
                    key={artwork.id} 
                    artwork={artwork}
                    onOpen={handleOpenArtwork}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
      <ArtworkModal
        isOpen={showArtworkModal}
        artwork={selectedArtwork}
        selectedSize={selectedArtwork ? selectedSizes[selectedArtwork.id] : undefined}
        onSizeSelect={handleSizeSelect}
        onAddToCart={handleAddToCart}
        isSubmitting={addingToCart}
        onClose={() => {
          setShowArtworkModal(false);
          setSelectedArtwork(null);
        }}
      />
    </div>
  );
};

export default SearchResults;
